/**
 * Patients repository.
 *
 * Every query that touches the `patients` table lives here. Components import
 * these functions and use them with React Query — no component should call
 * `supabase.from('patients')` directly.
 *
 * See `src/data/README.md` for the rules of the repository layer.
 */

import { supabase } from '@/integrations/supabase/client';
import { unwrap, unwrapMaybe } from '@/lib/supabase-helpers';
import { logAudit } from '@/lib/audit';
import type {
  Tables,
  TablesInsert,
  TablesUpdate,
} from '@/integrations/supabase/types';

/**
 * Actor-Kontext fuer DSGVO-Audit-Logs. Jede mutierende Repository-Funktion
 * verlangt diesen Parameter, damit jede Veraenderung von Patientendaten
 * einen nachvollziehbaren Urheber erhaelt (Art. 5(2) Rechenschaftspflicht).
 */
export interface PatientActor {
  userId: string;
  organizationId: string;
}

export type Patient = Tables<'patients'>;
export type PatientInsert = TablesInsert<'patients'>;
export type PatientUpdate = TablesUpdate<'patients'>;

export type RecentPatient = Pick<Patient, 'id' | 'first_name' | 'last_name' | 'icd10_label' | 'updated_at'>;

export const DEFAULT_RECENT_PATIENT_LIMIT = 5;
export const MAX_RECENT_PATIENT_LIMIT = 50;

export function normalizeRecentPatientsLimit(limit?: number): number {
  if (!Number.isFinite(limit)) return DEFAULT_RECENT_PATIENT_LIMIT;
  const normalized = Math.floor(limit as number);
  if (normalized < 1) return DEFAULT_RECENT_PATIENT_LIMIT;
  return Math.min(normalized, MAX_RECENT_PATIENT_LIMIT);
}

/**
 * Return the most recently updated active patients for a dashboard widget.
 */
export async function listRecentPatients(
  orgId: string,
  limit = DEFAULT_RECENT_PATIENT_LIMIT,
): Promise<RecentPatient[]> {
  const safeLimit = normalizeRecentPatientsLimit(limit);
  return unwrap(
    supabase
      .from('patients')
      .select('id, first_name, last_name, icd10_label, updated_at')
      .eq('organization_id', orgId)
      .or('is_deleted.is.null,is_deleted.eq.false')
      .order('updated_at', { ascending: false })
      .limit(safeLimit),
  );
}

export interface ListPatientsOptions {
  /** Search across first/last name + email, returns case-insensitive matches. */
  search?: string;
  /** When `true`, soft-deleted patients are included. */
  includeDeleted?: boolean;
  /** Filter by clinical admission status. */
  admissionStatus?: 'ambulant' | 'aufgenommen' | 'verlegt' | 'entlassen' | 'nachstationaer';
  /** Restrict to a specific therapist's caseload. */
  assignedTherapeutId?: string;
  /** Filter by insurance type (gkv | pkv | selbstzahler | unknown). */
  insuranceType?: string;
  /** Limit the number of returned rows. */
  limit?: number;
}

/**
 * List patients for an organisation, with optional filters.
 *
 * Sorting is server-side by `last_name` ASC so the UI gets a stable order
 * without paying for in-memory sorting.
 */
export async function listPatients(
  orgId: string,
  options: ListPatientsOptions = {},
): Promise<Patient[]> {
  let q = supabase
    .from('patients')
    .select('*')
    .eq('organization_id', orgId)
    .order('last_name', { ascending: true });

  if (!options.includeDeleted) q = q.or('is_deleted.is.null,is_deleted.eq.false');
  if (options.admissionStatus) q = q.eq('admission_status', options.admissionStatus);
  if (options.assignedTherapeutId) q = q.eq('assigned_therapeut_id', options.assignedTherapeutId);
  if (options.insuranceType) q = q.eq('insurance_type', options.insuranceType);
  if (options.search && options.search.trim().length > 0) {
    const term = options.search.trim().replace(/[%_]/g, '\\$&');
    q = q.or(
      `first_name.ilike.%${term}%,last_name.ilike.%${term}%,email.ilike.%${term}%`,
    );
  }
  if (options.limit && options.limit > 0) q = q.limit(options.limit);

  return unwrap(q);
}

/**
 * Fetch a single patient by id, scoped to an organisation. Returns `null`
 * when the patient does not exist (callers can show a not-found page).
 */
export async function getPatient(orgId: string, patientId: string): Promise<Patient | null> {
  return unwrapMaybe(
    supabase
      .from('patients')
      .select('*')
      .eq('id', patientId)
      .eq('organization_id', orgId)
      .maybeSingle(),
  );
}

/** Schmale Projektion für die Namens-Heuristik-Suche. */
export type PatientNameMatch = Pick<Patient, 'id' | 'first_name' | 'last_name'>;

/**
 * Heuristische Patientensuche aus Freitext (KI-Chat).
 *
 * Lädt die aktiven Patient:innen der Organisation und sucht den ersten,
 * dessen Name im übergebenen Text vorkommt — erst Vor- *und* Nachname,
 * dann Nachname allein, dann Vorname allein. Gibt `null` zurück, wenn
 * nichts passt.
 *
 * Die Heuristik ist bewusst Teil der Repo-Funktion: Vor diesem Helper
 * lag der `supabase.from('patients')`-Call direkt in `NutriChatSheet.tsx`
 * (Bruch der Repo-Disziplin, siehe `src/data/README.md`). Die
 * Matching-Logik gehört zur Operation „finde Patient:in im Text" dazu.
 */
export async function findPatientByNameInText(
  orgId: string,
  text: string,
): Promise<PatientNameMatch | null> {
  const rows = await unwrap(
    supabase
      .from('patients')
      .select('id, first_name, last_name')
      .eq('organization_id', orgId)
      .eq('is_deleted', false),
  );
  if (!rows || rows.length === 0) return null;
  const lower = text.toLowerCase();
  return (
    rows.find(
      (p) =>
        lower.includes(p.first_name.toLowerCase()) &&
        lower.includes(p.last_name.toLowerCase()),
    ) ??
    rows.find((p) => lower.includes(p.last_name.toLowerCase())) ??
    rows.find((p) => lower.includes(p.first_name.toLowerCase())) ??
    null
  );
}

/** Create a new patient and return the inserted row. */
export async function createPatient(
  input: PatientInsert,
  actor: PatientActor,
): Promise<Patient> {
  const row = await unwrap(
    supabase.from('patients').insert([input]).select('*').single(),
  );
  // DSGVO Art. 30 – Verarbeitungsverzeichnis. Fire-and-forget.
  void logAudit('patient_create', actor.userId, actor.organizationId, undefined, {
    patientId: row.id,
    resourceType: 'patient',
    resourceId: row.id,
  });
  return row;
}

/** Update an existing patient and return the new row. */
export async function updatePatient(
  patientId: string,
  patch: PatientUpdate,
  actor: PatientActor,
): Promise<Patient> {
  const row = await unwrap(
    supabase
      .from('patients')
      .update(patch)
      .eq('id', patientId)
      .select('*')
      .single(),
  );
  // Nur geaenderte Feldnamen (nicht deren Werte) loggen, um PHI aus dem
  // Audit-Trail herauszuhalten – der Trail dokumentiert, dass etwas geaendert
  // wurde, nicht den Klartext.
  void logAudit('patient_update', actor.userId, actor.organizationId, {
    changed_fields: Object.keys(patch),
  }, {
    patientId,
    resourceType: 'patient',
    resourceId: patientId,
  });
  return row;
}

/**
 * Count active patients in an organisation. Uses a HEAD request so
 * Supabase only returns the count, not the rows — used by the dashboard
 * KPI cards.
 */
export async function countActivePatients(orgId: string): Promise<number> {
  const { count, error } = await supabase
    .from('patients')
    .select('id', { count: 'exact', head: true })
    .eq('organization_id', orgId)
    .or('is_deleted.is.null,is_deleted.eq.false');
  if (error) throw error;
  return count ?? 0;
}

/**
 * Soft-delete a patient (DSGVO §17 + §630f BGB retention).
 *
 * The actual hard delete happens after the legal retention window expires —
 * this function just sets the tombstone.
 */
export async function softDeletePatient(
  patientId: string,
  actor: PatientActor,
): Promise<void> {
  await unwrap(
    supabase
      .from('patients')
      .update({
        is_deleted: true,
        deleted_at: new Date().toISOString(),
        deleted_by: actor.userId,
      })
      .eq('id', patientId)
      .select('id')
      .single(),
  );
  void logAudit('patient_delete', actor.userId, actor.organizationId, {
    soft: true,
  }, {
    patientId,
    resourceType: 'patient',
    resourceId: patientId,
  });
}
